import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ListChecks, Sparkles, Plus } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { SkeletonLoader, useToast } from "@/components/system";
import { usePrefs } from "@/lib/prefs-store";
import { startOfWeek as startOfWeekShared } from "@/lib/calendar-utils";

const WORKER_URL =
  (import.meta.env.VITE_HAUS_WORKER_URL as string | undefined) ??
  "https://haus-worker.rackljo.workers.dev";

type Entry = {
  id: string;
  date: string;
  meal_type: string;
  recipe_id: string | null;
  custom_meal_name: string | null;
  servings_planned: number;
  status: string;
  rationale: string | null;
};

const SLOTS = ["breakfast", "lunch", "dinner", "snack"] as const;

export function MealPlan() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const weekStart = usePrefs((s) => s.weekStart);
  // HOTFIX-BW19.4 PR-A Bug 5: derive the displayed week from today() on every
  // render and offset by user-controlled `weekOffset` (component state only).
  // This guarantees a hard reload always lands on the current week.
  const [weekOffset, setWeekOffset] = useState(0);
  const { start, days, isCurrentWeek } = useMemo(() => {
    const today = new Date();
    const currentStart = startOfWeekShared(today, weekStart);
    const s = new Date(currentStart);
    s.setDate(currentStart.getDate() + weekOffset * 7);
    const ds = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(s);
      d.setDate(s.getDate() + i);
      return d.toISOString().slice(0, 10);
    });
    return { start: s, days: ds, isCurrentWeek: weekOffset === 0 };
  }, [weekStart, weekOffset]);

  const entries = useQuery({
    queryKey: ["meal_plan", userId, days[0]],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("meal_plan_entry")
        .select("id, date, meal_type, recipe_id, custom_meal_name, servings_planned, status, rationale")
        .eq("user_id", userId!)
        .gte("date", days[0])
        .lte("date", days[days.length - 1]);
      if (error) throw error;
      return (data ?? []) as Entry[];
    },
  });

  async function generatePlan() {
    setGenerating(true);
    setGenerateError(null);
    try {
      const accessToken = (await supabase.auth.getSession()).data.session
        ?.access_token;
      const res = await fetch(`${WORKER_URL}/api/meal-plan/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ start_date: days[0] }),
      });
      const text = await res.text();
      let payload: { error?: string; message?: string; written?: number } | null =
        null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch {
        payload = null;
      }
      if (!res.ok) {
        const code = payload?.error ?? "";
        const msg = mealPlanErrorMessage(code, res.status, text);
        setGenerateError(msg);
        toast.show(msg);
        return;
      }
      if (payload?.written === 0) {
        const empty =
          "No meals were generated. Add a few recipes to your library first, then try again.";
        setGenerateError(empty);
        toast.show(empty);
        return;
      }
      toast.show(
        payload?.written
          ? `Generated ${payload.written} meals for the week.`
          : "Meal plan generated.",
      );
      queryClient.invalidateQueries({ queryKey: ["meal_plan", userId] });
    } catch (e) {
      const msg =
        e instanceof Error
          ? `Plan generation failed: ${e.message}`
          : `Plan generation failed: ${String(e)}`;
      setGenerateError(msg);
      toast.show(msg);
    } finally {
      setGenerating(false);
    }
  }

  const byKey = new Map<string, Entry>();
  for (const e of entries.data ?? []) {
    byKey.set(`${e.date}_${e.meal_type}`, e);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
      <header style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <ListChecks size={22} color="var(--accent)" />
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 28 }}>Meal plan</h1>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            marginLeft: 4,
          }}
        >
          <button
            type="button"
            aria-label="Previous week"
            onClick={() => setWeekOffset((n) => n - 1)}
            style={navButtonStyle}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ color: "var(--fg-3)", fontSize: 13, minWidth: 110, textAlign: "center" }}>
            {weekRangeLabel(start)}
          </span>
          <button
            type="button"
            aria-label="Next week"
            onClick={() => setWeekOffset((n) => n + 1)}
            style={navButtonStyle}
          >
            <ChevronRight size={16} />
          </button>
          <button
            type="button"
            onClick={() => setWeekOffset(0)}
            disabled={isCurrentWeek}
            style={{
              ...navButtonStyle,
              padding: "6px 10px",
              opacity: isCurrentWeek ? 0.4 : 1,
              cursor: isCurrentWeek ? "default" : "pointer",
            }}
          >
            This week
          </button>
        </div>
        <div style={{ flex: 1 }} />
        <button
          onClick={generatePlan}
          disabled={generating}
          style={{
            background: "var(--accent)",
            color: "var(--accent-on)",
            padding: "10px 16px",
            borderRadius: 10,
            border: "none",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Sparkles size={14} /> {generating ? "Generating…" : "Auto-generate"}
        </button>
      </header>

      {entries.isLoading ? (
        <SkeletonLoader variant="card" height={400} />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto repeat(7, 1fr)",
            gap: 4,
            border: "1px solid var(--hair)",
            borderRadius: "var(--radius-card)",
            background: "var(--bg-2)",
            overflowX: "auto",
            padding: 8,
          }}
        >
          <div />
          {days.map((d) => (
            <div
              key={d}
              style={{
                padding: 8,
                fontSize: 11,
                color: "var(--fg-3)",
                textAlign: "center",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {new Date(d).toLocaleDateString(undefined, { weekday: "short" })}
              <br />
              {new Date(d).getDate()}
            </div>
          ))}
          {SLOTS.map((slot) => (
            <SlotRow
              key={slot}
              slot={slot}
              days={days}
              entries={byKey}
              onClick={(date) => navigateAdd(date, slot)}
            />
          ))}
        </div>
      )}

      {generateError && (
        <div
          role="alert"
          style={{
            padding: "var(--space-3) var(--space-4)",
            borderRadius: "var(--radius-card)",
            background: "color-mix(in oklab, var(--brick) 12%, transparent)",
            border: "1px solid var(--brick)",
            color: "var(--fg)",
            fontSize: "var(--type-body-s)",
            lineHeight: 1.5,
          }}
        >
          {generateError}
        </div>
      )}

      <p style={{ color: "var(--fg-3)", fontSize: 12, margin: 0 }}>
        Tip — auto-generation considers Pantry expiry, training plan, athlete macros, and previously cooked recipes.
      </p>
    </div>
  );
}

function mealPlanErrorMessage(
  code: string,
  status: number,
  fallback: string,
): string {
  switch (code) {
    case "monthly_cap_exceeded":
      return "Monthly Anthropic spend cap reached. Try again next month or raise the cap in Settings.";
    case "no_plan_generated":
      return "Anthropic returned an empty plan — try again, or add a few recipes so it has something to work with.";
    case "extraction_failed":
      return "Anthropic couldn't parse a plan. Try again, or report this to support if it keeps failing.";
    case "anthropic not configured":
      return "Anthropic API key isn't configured on the server. Contact the operator.";
    default: {
      if (status === 401)
        return "You're not signed in. Refresh the page and try again.";
      if (status === 429)
        return "Too many requests — wait a moment and try again.";
      if (status >= 500)
        return `Server error (${status}). ${fallback?.slice(0, 200) || "Try again in a moment."}`;
      return (
        `Plan generation failed (${status}). ${
          code || fallback?.slice(0, 200) || "Unknown error."
        }`
      );
    }
  }
}

function SlotRow({
  slot,
  days,
  entries,
  onClick,
}: {
  slot: string;
  days: string[];
  entries: Map<string, Entry>;
  onClick: (date: string) => void;
}) {
  return (
    <>
      <div
        style={{
          padding: 8,
          fontSize: 11,
          color: "var(--fg-3)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {slot}
      </div>
      {days.map((d) => {
        const entry = entries.get(`${d}_${slot}`);
        return (
          <button
            key={d}
            onClick={() => onClick(d)}
            style={{
              padding: 6,
              minHeight: 60,
              border: "1px dashed var(--hair-2)",
              borderRadius: 6,
              background: entry ? "var(--chip)" : "transparent",
              color: "var(--fg)",
              cursor: "pointer",
              textAlign: "left",
              fontSize: 11,
              fontFamily: "inherit",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {entry ? (
              <>
                <span style={{ fontWeight: 500 }}>
                  {entry.custom_meal_name ?? entry.recipe_id ?? "(planned)"}
                </span>
                {entry.rationale && (
                  <span style={{ color: "var(--fg-3)", fontSize: 10 }}>{entry.rationale}</span>
                )}
              </>
            ) : (
              <Plus size={12} color="var(--fg-3)" />
            )}
          </button>
        );
      })}
    </>
  );
}

function navigateAdd(_date: string, _slot: string) {
  // Future: open recipe picker drawer. For v1, just navigate to recipes.
  window.location.href = "/recipes";
}

const navButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "6px",
  background: "transparent",
  color: "var(--fg-2)",
  border: "1px solid var(--hair)",
  borderRadius: 8,
  fontSize: "var(--type-body-s)",
  cursor: "pointer",
};

function weekRangeLabel(start: Date): string {
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${fmt(start)} to ${fmt(end)}`;
}

// Week-start logic now lives in @/lib/calendar-utils so the user setting feeds
// every weekly view consistently. This file no longer hard-codes Monday.
